import { useCallback, useEffect, useMemo, useState } from 'react';
import { CloudUploadOutlined, EditOutlined } from '@ant-design/icons';
import { ProCard } from '@ant-design/pro-components';
import { App, Button, DatePicker, Flex, Form, Popconfirm, Select, Space, Tag, Typography } from 'antd';
import { useSetState } from 'ahooks';
import dayjs, { type Dayjs } from 'dayjs';
import { FormattedMessage, useIntl } from 'react-intl';

import AdminProTable from '@/components/admin-pro-table';
import Permission from '@/components/permission';
import PublishEditModal from '@/pages/app-version-updates/components/PublishEditModal';
import type { ListSearchFilters, PublishEditModalState, TimePreset } from '@/pages/app-version-updates/types';
import {
    buildListParams,
    getConfiguredChannels,
    getStatusColor,
    getUpdateTypeColor,
    truncateText,
} from '@/pages/app-version-updates/utils';
import {
    appVersionUpdateDisable,
    appVersionUpdateEnable,
    appVersionUpdateList,
    type AppVersionUpdateItem,
} from '@/services/app-version-updates';

import type { ProColumns } from '@ant-design/pro-components';
import type { PaginationProps } from 'antd';

const PERMISSION = 'Client-Config_App-Version';
const CREATE_PERMISSION = 'Client-Config_App-Version_Create';
const UPDATE_PERMISSION = 'Client-Config_App-Version_Update';

const DEFAULT_PAGINATION: PaginationProps = {
    current: 1,
    pageSize: 20,
};

type SearchFormValues = {
    timePreset?: TimePreset;
    created_at_range?: [Dayjs, Dayjs];
    channel?: ListSearchFilters['channel'];
    type?: ListSearchFilters['type'];
    status?: ListSearchFilters['status'];
};

const PageAppVersionUpdates: React.FC = () => {
    const { message } = App.useApp();
    const { formatMessage } = useIntl();
    const [searchForm] = Form.useForm<SearchFormValues>();

    const [pagination, setPagination] = useSetState<PaginationProps>({ ...DEFAULT_PAGINATION });
    const [loading, setLoading] = useState(false);
    const [dataSource, setDataSource] = useState<AppVersionUpdateItem[]>([]);
    const [searchFilters, setSearchFilters] = useState<ListSearchFilters>({ timePreset: 'all' });
    const [modalState, setModalState] = useSetState<PublishEditModalState>({
        open: false,
        mode: 'create',
    });

    const fetchList = useCallback(async () => {
        setLoading(true);
        try {
            const page = pagination.current ?? DEFAULT_PAGINATION.current!;
            const size = pagination.pageSize ?? DEFAULT_PAGINATION.pageSize!;
            const response = await appVersionUpdateList(buildListParams(searchFilters, page, size));
            const { data } = response;

            if (data.code !== 200) {
                message.error(data.msg);
                return;
            }

            setDataSource(data.data ?? []);
            if (data.meta) {
                setPagination({
                    current: Number(data.meta.page) > 0 ? Number(data.meta.page) : page,
                    pageSize: Number(data.meta.size) > 0 ? Number(data.meta.size) : size,
                    total: data.meta.total ?? data.data?.length ?? 0,
                });
            } else {
                setPagination((prev) => ({
                    ...prev,
                    total: data.data?.length ?? 0,
                }));
            }
        } catch (error: any) {
            message.error(error?.response?.data?.msg || error?.message || formatMessage({ id: 'appVersionUpdates.message.loadFailed' }));
        } finally {
            setLoading(false);
        }
    }, [formatMessage, message, pagination.current, pagination.pageSize, searchFilters, setPagination]);

    useEffect(() => {
        fetchList();
    }, [fetchList]);

    const handleSearch = useCallback(() => {
        const values = searchForm.getFieldsValue();
        const nextFilters: ListSearchFilters = {
            timePreset: values.timePreset ?? 'all',
            channel: values.channel ?? '',
            type: values.type ?? '',
            status: values.status ?? '',
        };
        if (values.timePreset === 'custom' && values.created_at_range?.[0] && values.created_at_range?.[1]) {
            nextFilters.created_at_range = [
                values.created_at_range[0].startOf('day').toISOString(),
                values.created_at_range[1].endOf('day').toISOString(),
            ];
        }
        setPagination((prev) => ({ ...prev, current: 1 }));
        setSearchFilters(nextFilters);
    }, [searchForm, setPagination]);

    const handleResetSearch = useCallback(() => {
        searchForm.resetFields();
        setPagination((prev) => ({ ...prev, current: 1 }));
        setSearchFilters({ timePreset: 'all' });
    }, [searchForm, setPagination]);

    const openCreateModal = useCallback(() => {
        setModalState({ open: true, mode: 'create', record: undefined });
    }, [setModalState]);

    const openEditModal = useCallback(
        (record: AppVersionUpdateItem) => {
            setModalState({ open: true, mode: 'edit', record });
        },
        [setModalState],
    );

    const closeModal = useCallback(() => {
        setModalState({ open: false, mode: 'create', record: undefined });
    }, [setModalState]);

    const handleToggleStatus = useCallback(
        async (record: AppVersionUpdateItem) => {
            try {
                const response =
                    record.status === 'Enabled'
                        ? await appVersionUpdateDisable(record.id)
                        : await appVersionUpdateEnable(record.id);
                const { data } = response;
                if (data.code !== 200) {
                    message.error(data.msg);
                    return;
                }
                message.success(formatMessage({ id: 'appVersionUpdates.message.statusUpdated' }));
                fetchList();
            } catch (error: any) {
                message.error(error?.response?.data?.msg || error?.message || formatMessage({ id: 'appVersionUpdates.message.saveFailed' }));
            }
        },
        [fetchList, formatMessage, message],
    );

    const timePreset = Form.useWatch('timePreset', searchForm);

    const columns = useMemo<ProColumns<AppVersionUpdateItem>[]>(
        () => [
            {
                title: <FormattedMessage id="appVersionUpdates.table.idTime" />,
                dataIndex: 'id',
                width: 220,
                render: (_, record) => (
                    <Space direction="vertical" size={2}>
                        <Typography.Text code>{record.id}</Typography.Text>
                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                            {record.created_at ? dayjs(record.created_at).format('YYYY-MM-DD HH:mm:ss') : '-'}
                        </Typography.Text>
                    </Space>
                ),
            },
            {
                title: <FormattedMessage id="appVersionUpdates.table.channels" />,
                dataIndex: 'ios_version_name',
                width: 240,
                render: (_, record) => (
                    <Space direction="vertical" size={6}>
                        {getConfiguredChannels(record).map(({ key, version }) => (
                            <Space key={key} size={6}>
                                <Tag>
                                    <FormattedMessage id={`appVersionUpdates.channel.${key}.short`} />
                                </Tag>
                                <Typography.Text code>{version}</Typography.Text>
                            </Space>
                        ))}
                    </Space>
                ),
            },
            {
                title: <FormattedMessage id="appVersionUpdates.table.type" />,
                dataIndex: 'type',
                width: 120,
                render: (_, record) => (
                    <Tag color={getUpdateTypeColor(record.type)}>
                        <FormattedMessage id={`appVersionUpdates.type.${record.type}.title`} />
                    </Tag>
                ),
            },
            {
                title: <FormattedMessage id="appVersionUpdates.table.status" />,
                dataIndex: 'status',
                width: 100,
                render: (_, record) => (
                    <Tag color={getStatusColor(record.status)}>
                        <FormattedMessage id={`appVersionUpdates.status.${record.status}`} />
                    </Tag>
                ),
            },
            {
                title: <FormattedMessage id="appVersionUpdates.table.contents" />,
                dataIndex: 'contents',
                width: 320,
                render: (_, record) => (
                    <Space direction="vertical" size={4}>
                        <Typography.Text>
                            <Tag>ZH</Tag> {truncateText(record.contents?.zh)}
                        </Typography.Text>
                        <Typography.Text type="secondary">
                            <Tag>EN</Tag> {truncateText(record.contents?.en)}
                        </Typography.Text>
                    </Space>
                ),
            },
            {
                title: <FormattedMessage id="appVersionUpdates.table.publisher" />,
                dataIndex: 'publisher_account',
                width: 120,
                render: (_, record) => record.publisher_account || '-',
            },
            {
                title: <FormattedMessage id="t.c.action" />,
                key: 'action',
                width: 180,
                fixed: 'right',
                render: (_, record) => (
                    <Space size={8}>
                        <Permission permission={UPDATE_PERMISSION}>
                            <Button type="link" size="small" icon={<EditOutlined />} onClick={() => openEditModal(record)}>
                                <FormattedMessage id="b.edit" />
                            </Button>
                        </Permission>
                        <Permission permission={UPDATE_PERMISSION}>
                            <Popconfirm
                                title={
                                    record.status === 'Enabled' ? (
                                        <FormattedMessage id="appVersionUpdates.action.disableConfirm" />
                                    ) : (
                                        <FormattedMessage id="appVersionUpdates.action.enableConfirm" />
                                    )
                                }
                                onConfirm={() => handleToggleStatus(record)}
                            >
                                <Button type="link" size="small">
                                    <FormattedMessage
                                        id={
                                            record.status === 'Enabled'
                                                ? 'appVersionUpdates.action.disable'
                                                : 'appVersionUpdates.action.enable'
                                        }
                                    />
                                </Button>
                            </Popconfirm>
                        </Permission>
                    </Space>
                ),
            },
        ],
        [handleToggleStatus, openEditModal],
    );

    return (
        <Permission permission={PERMISSION}>
            <ProCard title={<FormattedMessage id="appVersionUpdates.title" />}>
                <Typography.Paragraph type="secondary" style={{ marginTop: -4, marginBottom: 16 }}>
                    <FormattedMessage id="appVersionUpdates.description" />
                </Typography.Paragraph>

                <Form form={searchForm} initialValues={{ timePreset: 'all' }} onFinish={handleSearch} style={{ marginBottom: 16 }}>
                    <Flex gap={16} wrap>
                        <Form.Item name="timePreset" style={{ marginBottom: 0 }}>
                            <Select
                                style={{ width: 160 }}
                                options={[
                                    { value: 'all', label: formatMessage({ id: 'appVersionUpdates.filter.timeAll' }) },
                                    { value: '7d', label: formatMessage({ id: 'appVersionUpdates.filter.time7d' }) },
                                    { value: '30d', label: formatMessage({ id: 'appVersionUpdates.filter.time30d' }) },
                                    { value: 'custom', label: formatMessage({ id: 'appVersionUpdates.filter.timeCustom' }) },
                                ]}
                            />
                        </Form.Item>
                        {timePreset === 'custom' ? (
                            <Form.Item name="created_at_range" style={{ marginBottom: 0 }}>
                                <DatePicker.RangePicker />
                            </Form.Item>
                        ) : null}
                        <Form.Item name="channel" style={{ marginBottom: 0 }}>
                            <Select
                                allowClear
                                style={{ width: 160 }}
                                placeholder={formatMessage({ id: 'appVersionUpdates.filter.all' })}
                                options={[
                                    { value: 'ios', label: formatMessage({ id: 'appVersionUpdates.channel.ios.label' }) },
                                    { value: 'android', label: formatMessage({ id: 'appVersionUpdates.channel.android.label' }) },
                                    { value: 'apk', label: formatMessage({ id: 'appVersionUpdates.channel.apk.label' }) },
                                ]}
                            />
                        </Form.Item>
                        <Form.Item name="type" style={{ marginBottom: 0 }}>
                            <Select
                                allowClear
                                style={{ width: 160 }}
                                placeholder={formatMessage({ id: 'appVersionUpdates.filter.all' })}
                                options={[
                                    { value: 'Remind', label: formatMessage({ id: 'appVersionUpdates.type.Remind.title' }) },
                                    { value: 'Force', label: formatMessage({ id: 'appVersionUpdates.type.Force.title' }) },
                                    { value: 'RemindOnly', label: formatMessage({ id: 'appVersionUpdates.type.RemindOnly.title' }) },
                                ]}
                            />
                        </Form.Item>
                        <Form.Item name="status" style={{ marginBottom: 0 }}>
                            <Select
                                allowClear
                                style={{ width: 160 }}
                                placeholder={formatMessage({ id: 'appVersionUpdates.filter.all' })}
                                options={[
                                    { value: 'Enabled', label: formatMessage({ id: 'appVersionUpdates.status.Enabled' }) },
                                    { value: 'Disabled', label: formatMessage({ id: 'appVersionUpdates.status.Disabled' }) },
                                ]}
                            />
                        </Form.Item>
                        <Button type="primary" htmlType="submit" loading={loading}>
                            <FormattedMessage id="b.search" />
                        </Button>
                        <Button onClick={handleResetSearch}>
                            <FormattedMessage id="b.reset" />
                        </Button>
                    </Flex>
                </Form>

                <AdminProTable<AppVersionUpdateItem>
                    bordered
                    rowKey="id"
                    loading={loading}
                    columns={columns}
                    dataSource={dataSource}
                    search={false}
                    cardProps={{ bodyStyle: { padding: 0 } }}
                    toolBarRender={() => [
                        <Permission key="publish" permission={CREATE_PERMISSION}>
                            <Button type="primary" icon={<CloudUploadOutlined />} onClick={openCreateModal}>
                                <FormattedMessage id="appVersionUpdates.action.publish" />
                            </Button>
                        </Permission>,
                    ]}
                    pagination={{
                        ...pagination,
                        showSizeChanger: true,
                        onChange: (page, pageSize) => {
                            setPagination({ current: page, pageSize });
                        },
                    }}
                    options={{
                        reload: () => fetchList(),
                        density: true,
                        setting: true,
                    }}
                    scroll={{ x: 1300 }}
                />
            </ProCard>

            <PublishEditModal
                open={modalState.open}
                mode={modalState.mode}
                recordId={modalState.record?.id}
                onClose={closeModal}
                onSuccess={fetchList}
            />
        </Permission>
    );
};

export default PageAppVersionUpdates;
